import { AsyncPaginate } from "react-select-async-paginate";
import { useQueryClient } from "@tanstack/react-query";
import { axiosInstance } from "@retro-ia/shared/axios";
import { useEffect, useState } from "react";
import type { LoadOptions } from "react-select-async-paginate";
import type { GroupBase, StylesConfig } from "react-select";

interface ProgramaOption {
  label: string;
  value: string;
}

interface SelectProgramaAsyncProps {
  value: ProgramaOption | null | string;
  onChange: (option: ProgramaOption | null) => void;
  disabled?: boolean;
  placeholder?: string;
}

export function SelectPrograma({
  value,
  onChange,
  disabled,
  placeholder = "Selecciona un programa",
}: SelectProgramaAsyncProps) {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<ProgramaOption | null>(
    value && typeof value === "object" ? (value as ProgramaOption) : null
  );

  useEffect(() => {
    // Si el value es un objeto con label, sincroniza el estado local
    if (value && typeof value === "object" && "label" in value) {
      if (!selected || selected.value !== value.value) {
        setSelected(value as ProgramaOption);
      }
      return;
    }
    // Si el value es un id, busca el label
    if (value && (typeof value === "string" || typeof value === "number")) {
      const id = String(value);
      if (!id || id.trim() === "") {
        setSelected(null);
        return;
      }
      (async () => {
        try {
          const { data } = await axiosInstance.get(`/programas/${id}`);
          setSelected({ value: String(data.id), label: data.nombre });
        } catch {
          setSelected(null);
        }
      })();
      return;
    }
    // Si no hay value, no actualices el estado local
    // No sobrescribas el estado local a null si ya hay una selección
  }, [value]);

  const loadOptions: LoadOptions<
    ProgramaOption,
    GroupBase<ProgramaOption>,
    { page: number }
  > = async (inputValue, loadedOptions, additional) => {
    const page = additional?.page ?? 1;
    const res = await queryClient.fetchQuery({
      queryKey: ["programas-select", inputValue, page],
      queryFn: async () => {
        const r = await axiosInstance.get("/programas/select-options", {
          params: { search: inputValue, page, limit: 20 },
        });
        return r.data;
      },
    });

    type Programa = { id: number; nombre: string };
    const options = res.data.map((programa: Programa) => ({
      label: programa.nombre,
      value: String(programa.id),
    }));

    return {
      options,
      hasMore: loadedOptions.length + options.length < res.total,
      additional: { page: page + 1 },
    };
  };

  const customStyles: StylesConfig<ProgramaOption, false> = {
    control: (base, state) => ({
      ...base,
      backgroundColor: "var(--background)",
      borderColor: state.isFocused ? "var(--ring)" : "var(--border)",
      boxShadow: state.isFocused ? "0 0 0 2px var(--ring)" : "none",
      borderRadius: "0.5rem",
      minHeight: "2.5rem",
      padding: "0 0.25rem",
      color: "var(--foreground)",
      transition: "border-color 0.2s, box-shadow 0.2s",
    }),
    placeholder: (base) => ({
      ...base,
      color: "var(--foreground)",
    }),
    singleValue: (base) => ({
      ...base,
      color: "var(--foreground)",
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: "var(--background)",
      border: "1px solid var(--muted)",
      borderRadius: "0.5rem",
      boxShadow: "0 4px 8px rgba(0,0,0,0.08)",
      zIndex: 20,
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused ? "var(--primary)" : "var(--background)",
      color: state.isFocused ? "var(--muted)" : "var(--foreground)",
      padding: "0.5rem 0.75rem",
      cursor: "pointer",
    }),
    input: (base) => ({
      ...base,
      color: "var(--foreground)",
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: "var(--muted-foreground)",
    }),
  };

  return (
    <AsyncPaginate
      value={selected}
      loadOptions={loadOptions}
      onChange={(option) => {
        setSelected(option);
        // Envía solo el id al formulario (como string)
        onChange(option ? option : null);
      }}
      isDisabled={disabled}
      placeholder={placeholder}
      additional={{ page: 1 }}
      debounceTimeout={300}
      styles={customStyles}
    />
  );
}
